/**
 * Edit Company Modal
 * Allows Super Admin to update company information
 */
import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Select,
  InputNumber,
  Space,
  Switch,
} from 'antd';
import {
  EditOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import superAdminService, { Company } from '../../services/superAdminService';
import '../../styles/modalStyles.css';

const { TextArea } = Input;
const { Option } = Select;

interface EditCompanyModalProps {
  visible: boolean;
  company: Company | null;
  sidebarCollapsed?: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditCompanyModal: React.FC<EditCompanyModalProps> = ({
  visible,
  company,
  sidebarCollapsed = false,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (visible && company) {
      form.setFieldsValue({
        name: company.name,
        description: company.description,
        email: company.email,
        phone: company.phone,
        address: company.address,
        country: company.country,
        corporate_residence: company.corporate_residence,
        registration_number: company.registration_number,
        tax_id: company.tax_id,
        plan_type: company.plan_type,
        max_users: company.max_users,
        max_locations: company.max_locations,
        is_active: company.is_active,
      });
    }
  }, [visible, company, form]);

  const handleSubmit = async (values: any) => {
    if (!company) return;

    setLoading(true);
    try {
      const updateData: Partial<Company> = {
        name: values.name,
        description: values.description,
        email: values.email,
        phone: values.phone,
        address: values.address,
        country: values.country,
        corporate_residence: values.corporate_residence,
        registration_number: values.registration_number,
        tax_id: values.tax_id,
        plan_type: values.plan_type,
        max_users: values.max_users,
        max_locations: values.max_locations,
        is_active: values.is_active,
      };

      await superAdminService.updateCompany(company.id, updateData);
      message.success('Company updated successfully!');
      onSuccess();
    } catch (error: any) {
      message.error(error.message || 'Failed to update company');
      console.error('Error updating company:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!company) return null;

  return (
    <Modal
      title={
        <Space>
          <EditOutlined />
          <span>Edit Company - {company.name}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      getContainer={() => document.querySelector('.main-content-full') || document.body}
      wrapClassName="form-panel-full-wrap"
      width="100%"
      style={{ maxWidth: 'none' }}
      destroyOnClose
      zIndex={2000}
      footer={null}
      bodyStyle={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          plan_type: company.plan_type || 'basic',
          max_users: company.max_users,
          max_locations: company.max_locations,
          is_active: company.is_active,
        }}
        autoComplete="off"
      >
        <Form.Item
          label="Company Name"
          name="name"
          rules={[{ required: true, message: 'Please enter company name' }]}
        >
          <Input autoComplete="off" placeholder="Enter company name" />
        </Form.Item>

        <Form.Item
          label="Company Code"
        >
          <Input value={company.code} disabled autoComplete="off" />
          <div style={{ marginTop: 4, fontSize: '12px', color: '#999' }}>
            Company code cannot be changed
          </div>
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
        >
          <TextArea autoComplete="off" rows={2} placeholder="Enter company description" />
        </Form.Item>

        <Form.Item
          label="Country"
          name="country"
        >
          <Input autoComplete="off" placeholder="Enter country" prefix={<GlobalOutlined />} />
        </Form.Item>

        <Form.Item
          label="Residence of Corporation"
          name="corporate_residence"
        >
          <TextArea
            rows={2}
            placeholder="Enter residence of corporation"
          />
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
          rules={[{ type: 'email', message: 'Please enter a valid email' }]}
        >
          <Input autoComplete="off" placeholder="Enter company email" />
        </Form.Item>

        <Form.Item
          label="Phone"
          name="phone"
        >
          <Input autoComplete="off" placeholder="Enter company phone" />
        </Form.Item>

        <Form.Item
          label="Address"
          name="address"
        >
          <TextArea autoComplete="off" rows={2} placeholder="Enter company address" />
        </Form.Item>

        <Form.Item
          label="Registration Number"
          name="registration_number"
        >
          <Input autoComplete="off" placeholder="Enter registration number" />
        </Form.Item>

        <Form.Item
          label="Tax ID"
          name="tax_id"
        >
          <Input autoComplete="off" placeholder="Enter tax ID" />
        </Form.Item>

        <Form.Item
          label="Plan Type"
          name="plan_type"
        >
          <Select
            onChange={(v) => {
              const plan = (v || 'basic').toString().toLowerCase();
              const presets: Record<string, { max_users: number; max_locations: number }> = {
                basic: { max_users: 2, max_locations: 1 }, // shown as Standard
                standard: { max_users: 2, max_locations: 1 },
                premium: { max_users: 6, max_locations: 4 },
                enterprise: { max_users: 15, max_locations: 6 },
              };
              const p = presets[plan] || presets.basic;
              form.setFieldsValue({ max_users: p.max_users, max_locations: p.max_locations });
            }}
          >
            <Option value="basic">Standard</Option>
            <Option value="premium">Premium</Option>
            <Option value="enterprise">Enterprise</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Max Users"
          name="max_users"
          rules={[{ required: true, message: 'Please enter max users' }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="Max Locations"
          name="max_locations"
          rules={[{ required: true, message: 'Please enter max locations' }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label="Status"
          name="is_active"
          valuePropName="checked"
        >
          <Switch
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              Update Company
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditCompanyModal;
